import { useContext, useEffect } from 'react';
import { graphql, useFragment, useRelayEnvironment } from 'react-relay';
import { getBuyerId } from 'dibs-cookie-jar';
import { isEmailOnly } from 'dibs-cookie-jar/exports/isEmailOnly';
import { loginUserWithEmailLoginToken } from 'dibs-buyer-layout/exports/loginUserWithEmailLoginToken';
import { getEmailLoginToken } from 'dibs-buyer-layout/exports/getEmailLoginToken';
import { isUserLoginRequired, getUserEmail } from '../../../helpers/checkoutUser';
import { getGuestUserToken } from '../../../helpers/checkoutGuestUserToken';
import { getCheckoutParam } from '../../../helpers/checkoutRedirects';
import { useAppDispatch } from '../../../reducers/root';
import { setAuthState } from '../../../actions/checkoutUIActions';
import { transactionBuyerChangedAction } from '../../../actions/commonActions';
import {
    openAuthModal,
    openGuestCheckoutEmailLinkSentModal,
} from '../../../actions/checkoutAuthActions';
import { CheckoutServerVarsContext } from '../../shared/CheckoutServerVarsContext';
import { useAuth_viewer$key } from './__generated__/useAuth_viewer.graphql';
import { useAuth_user$key } from './__generated__/useAuth_user.graphql';

const viewerFragment = graphql`
    fragment useAuth_viewer on Viewer {
        userTokenCanCheckout(userId: $buyerId) @include(if: $hasBuyerId)
    }
`;

const userFragment = graphql`
    fragment useAuth_user on User {
        ...checkoutAuthActions_buyer
        profile {
            email
        }
        socialNetworks {
            ... on FacebookSocialNetwork {
                status
                socialId
                socialNetwork {
                    name
                }
            }
            ... on GoogleSocialNetwork {
                status
                socialId
                socialNetwork {
                    name
                }
            }
            ... on AppleSocialNetwork {
                status
                socialId
                socialNetwork {
                    name
                }
            }
        }
    }
`;

export type UseAuthParams = {
    viewer: useAuth_viewer$key | null;
    user: useAuth_user$key | null;
};

export const useAuth = ({ viewer: viewerRef, user: userRef }: UseAuthParams): void => {
    const dispatch = useAppDispatch();
    const environment = useRelayEnvironment();

    const { isMobile } = useContext(CheckoutServerVarsContext);

    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);

    useEffect(() => {
        if (!viewer) {
            return;
        }

        const { userTokenCanCheckout } = viewer;

        const processLogin = async (
            loginPromise: ReturnType<typeof loginUserWithEmailLoginToken>
        ): Promise<void> => {
            try {
                dispatch(setAuthState('LOADING'));
                const { isPageReloading } = await loginPromise;
                if (!isPageReloading) {
                    dispatch(setAuthState('CHECKED'));
                }
            } catch (e) {
                dispatch(setAuthState('CHECKED'));
            }
        };

        const isLoginRequired = isUserLoginRequired();
        const isEmailOnlyUser = isEmailOnly(document.cookie);
        const isGuestUser = !!getGuestUserToken();
        const emailLoginToken = getEmailLoginToken();

        if (isLoginRequired && getCheckoutParam('requestPasswordEmail')) {
            const email = getUserEmail();
            openGuestCheckoutEmailLinkSentModal({ email, environment, dispatch });
        } else if (
            // ensure userTokenCanCheckout is a boolean,
            // this may run before viewer is refetched when user logs in
            userTokenCanCheckout === false &&
            user &&
            !isEmailOnlyUser &&
            !isGuestUser
        ) {
            if (emailLoginToken) {
                // log in in a stale user through email login link
                processLogin(loginUserWithEmailLoginToken(environment, emailLoginToken, () => {}));
                return;
            } else {
                openAuthModal({ buyer: user, dispatch, environment });
            }
        } else if (!user && emailLoginToken) {
            // log in a user through email login link
            // when user has closed authUser modal and in result logged out
            processLogin(
                loginUserWithEmailLoginToken(environment, emailLoginToken, () => {
                    const buyerId = getBuyerId(document.cookie);
                    dispatch(
                        transactionBuyerChangedAction({
                            buyerId,
                        })
                    );
                })
            );
            return;
        }

        dispatch(setAuthState('CHECKED'));
    }, [dispatch, environment, isMobile, viewer, user]);
};
